import { useEffect, useRef, useState } from "react";
import {
  languagesDataType,
  ServicesDataType,
} from "../../interfaces/admin.interface";
import { Helmet } from "react-helmet";
import { message, Modal, Popconfirm, Switch, Table, TableProps } from "antd";
import * as Yup from "yup";
import { useFormik } from "formik";
import { LanguageService } from "../../services/languages.service";
import ErrorMessage from "../../components/shared/ErrorMessage/ErrorMessage";
import MainButton from "../../components/shared/MainButton/MainButton";
import Spinner from "../../components/shared/Spinner/Spinner";
import { ServicesService } from "../../services/services.service";
import ChangeLanguage from "../../components/shared/change-language/ChangeLanguage";
import ShowImage from "../../components/shared/show-image/ShowImage";
import SelectImage from "../../components/shared/select-image/SelectImage";
import { FaPlus } from "react-icons/fa";
import SelectColor from "../../components/admin-pages/products/select-color/SelectColor";
import SelectServicesGroupName from "../../components/services/SelectGroupNames";

const Services = () => {
  const [services, setServices] = useState<ServicesDataType[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editingRecord, setEditingRecord] = useState<ServicesDataType | null>(
    null
  );

  const [selectedColorName, setSelectedColorName] = useState<
    string | undefined
  >(undefined);

  const [langs, setLangs] = useState<languagesDataType[]>([]);
  const [primaryLangCode, setPrimaryLangCode] = useState<string>("UZ");
  const [langCode, setLangCode] = useState<string>("UZ");

  const [logoFileList, setLogoFileList] = useState<any[]>([]);
  const [showImageModal, setShowImageModal] = useState<boolean>(false);
  const [selectedImage, setSelectedImage] = useState<string>("");

  const [selectedName, setSelectedName] = useState<string | undefined>(
    undefined
  );

  const initialSpecsValues = useRef({
    name: "",
    description: "",
    text: "",
    price: 0,
    bg_color: 0,
    service_group_id: 0,
    discounted_price: 0,
    cover_image: "",
    is_visible: false,
    translations: [] as {
      lang_code: string;
      name: string;
      text: string;
      description: string;
    }[],
  });

  useEffect(() => {
    setLoading(true);
    ServicesService.getAllServices()
      .then((response) => {
        if (Array.isArray(response?.data?.data)) {
          const dataWithKeys = response?.data?.data?.map(
            (item: ServicesDataType, index: number) => ({
              ...item,
              key: index + 1,
            })
          );
          setServices(dataWithKeys);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    LanguageService.getAllLanguages().then((response) => {
      const data: languagesDataType[] = response?.data ?? [];
      setLangs(data);

      const foundUZ =
        data.find((l) => String(l.code).toUpperCase() === "UZ")?.code ?? null;

      const selectedPrimary = foundUZ ?? data?.[0]?.code ?? "UZ";
      setPrimaryLangCode(selectedPrimary);
      setLangCode(selectedPrimary);
    });
  }, []);

  const handleAfterSubmit = async () => {
    setLoading(true);
    setIsModalVisible(false);
    resetForm();
    const updatedData = await ServicesService.getAllServices();
    setServices(
      (updatedData?.data?.data ?? []).map(
        (item: ServicesDataType, index: number) => ({
          ...item,
          key: index + 1,
        })
      )
    );
    setLoading(false);
  };

  const {
    handleSubmit,
    handleChange,
    values,
    resetForm,
    setFieldValue,
    touched,
    errors,
  } = useFormik({
    initialValues: {
      name: "",
      description: "",
      bg_color: 0,
      text: "",
      translations: [] as {
        lang_code: string;
        name: string;
        text: string;
        description: string;
      }[],
      price: null as number | null,
      discounted_price: null as number | null,
      service_group_id: 0,
      cover_image: "",
      is_visible: false,
      selected_service: false,
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Servis adı tələb olunur"),
      description: Yup.string().required("Servis təsviri tələb olunur"),
      cover_image: Yup.string().required("Şəkil tələb olunur"),
    }),
    onSubmit: async (vals) => {
      // ✅ primary dil üçün əsas translation (AZ yoxdur)
      const primaryTranslation = {
        lang_code: primaryLangCode,
        name: vals.name,
        text: vals.text,
        description: vals.description,
      };

      const updatedTranslations = [
        primaryTranslation,
        ...(vals.translations || []).filter(
          (t) => t.lang_code !== primaryLangCode
        ),
      ];

      const finalValues = {
        ...vals,
        translations: updatedTranslations,
      };

      if (isEditing && editingRecord) {
        const isEqual =
          initialSpecsValues.current.name === finalValues.name &&
          initialSpecsValues.current.description === finalValues.description &&
          initialSpecsValues.current.text === finalValues.text &&
          initialSpecsValues.current.price === finalValues.price &&
          initialSpecsValues.current.discounted_price ===
            finalValues.discounted_price &&
          initialSpecsValues.current.bg_color === finalValues.bg_color &&
          initialSpecsValues.current.service_group_id ===
            finalValues.service_group_id &&
          initialSpecsValues.current.cover_image === finalValues.cover_image &&
          initialSpecsValues.current.is_visible === finalValues.is_visible &&
          JSON.stringify(finalValues.translations) ===
            JSON.stringify(initialSpecsValues.current.translations);

        if (isEqual) {
          message.info("Dəyişiklik edilmədi.");
        } else {
          if (editingRecord.cover_image === vals.cover_image) {
            const payload = {
              name: vals.name,
              description: vals.description,
              is_visible: vals.is_visible,
              text: vals.text,
              price: vals.price,
              bg_color: vals.bg_color,
              service_group_id: vals.service_group_id,
              discounted_price: vals.discounted_price,
              selected_service: vals.selected_service,
              translations: updatedTranslations,
            };

            const response = await ServicesService.updateService({
              ...payload,
              id: editingRecord.id,
            });

            if (response.status === 200) {
              message.success("Servis uğurla yeniləndi!");
              await handleAfterSubmit();
            } else {
              message.error(
                response.errorMessage || "Servisi yeniləmək alınmadı."
              );
            }
          } else {
            const response = await ServicesService.updateService({
              ...finalValues,
              id: editingRecord.id,
            });

            if (response.status === 200) {
              message.success("Servis uğurla yeniləndi!");
              await handleAfterSubmit();
            } else {
              message.error(
                response.errorMessage || "Servisi yeniləmək alınmadı."
              );
            }
          }
        }
      } else {
        const response = await ServicesService.addService(finalValues);
        if (response.status === 200) {
          message.success("Setvis uğurla əlavə edildi!");
          await handleAfterSubmit();
        } else {
          message.error(
            response.errorMessage || "Servis əlavə etmək alınmadı."
          );
        }
      }

      setLoading(false);
    },
  });

  const handleTranslationChange = (value: string, field: string) => {
    if (langCode === primaryLangCode) {
      setFieldValue(field, value);
    } else {
      const updatedTranslations = [...(values.translations || [])];
      const existingIndex = updatedTranslations.findIndex(
        (t) => t.lang_code === langCode
      );

      if (existingIndex !== -1) {
        updatedTranslations[existingIndex] = {
          ...updatedTranslations[existingIndex],
          [field]: value,
        };
      } else {
        updatedTranslations.push({
          lang_code: langCode,
          name: field === "name" ? value : "",
          text: field === "text" ? value : "",
          description: field === "description" ? value : "",
        });
      }

      setFieldValue("translations", updatedTranslations);
    }
  };

  const handleDeleteClick = async (record: ServicesDataType) => {
    try {
      await ServicesService.deleteService(record.id.toString());
      setServices((prevData) =>
        prevData.filter((item) => item.id !== record.id)
      );
      message.success("Servis uğurla silindi!");
    } catch (error) {
      message.error("Servisni silmək alınmadı.");
    }
  };

  const handleSwitchChange = async (
    checked: boolean,
    recordId: number,
    fieldName: string
  ) => {
    ServicesService.updateService({
      id: recordId,
      [fieldName]: checked,
    });

    if (fieldName === "is_visible") {
      if (checked) message.success("Məhsul artıq saytda görünür.");
      else message.info("Məhsul artıq saytda görünmür.");
    } else if (fieldName === "selected_service") {
      if (checked) message.success("Seçilmiş xidmət olaraq qeyd olundu.");
      else message.info("Seçilmiş xidmətlər siyahısından çıxarıldı.");
    }

    setServices((prevData) =>
      prevData.map((item) =>
        item.id === recordId ? { ...item, [fieldName]: checked } : item
      )
    );
  };

  const columns: TableProps<ServicesDataType>["columns"] = [
    {
      title: "№",
      dataIndex: "key",
      sorter: (a, b) => (a.key ?? 0) - (b.key ?? 0),
    },
    {
      title: "Servis adı",
      dataIndex: "name",
      sorter: (a, b) => (a.name || "").localeCompare(b.name || ""),
    },
    {
      title: "Servis təsviri",
      dataIndex: "description",
      render: (_, record) => (
        <div>
          {(record.description || "").length > 20
            ? (record.description || "").substring(0, 20) + "..."
            : record.description || ""}
        </div>
      ),
    },
    {
      title: "Mətn",
      dataIndex: "text",
      render: (text) => <div className="text-cell"> {text} </div>,
    },
    {
      title: "Qiymət",
      dataIndex: "price",
    },
    {
      title: "Endirim",
      dataIndex: "discounted_price",
    },
    {
      title: "Şəkil",
      dataIndex: "cover_image",
      render: (_, record) => (
        <div
          style={{ cursor: "pointer" }}
          onClick={() => {
            setSelectedImage(record.cover_image);
            setShowImageModal(true);
          }}
        >
          {record.cover_image ? (
            <img
              style={{ width: "40px" }}
              src={record.cover_image}
              alt={`${record.name || ""}`}
            />
          ) : null}
        </div>
      ),
    },
    {
      title: "Görünürlük",
      dataIndex: "is_visible",
      render: (_, record) => (
        <div className="switch_input">
          <Switch
            checked={record.is_visible}
            onChange={(checked) =>
              handleSwitchChange(checked, record.id, "is_visible")
            }
          />
        </div>
      ),
    },
    {
      title: "Seçilmiş xidmət",
      dataIndex: "selected_service",
      render: (_, record) => (
        <div className="switch_input">
          <Switch
            checked={record.selected_service}
            onChange={(checked) =>
              handleSwitchChange(checked, record.id, "selected_service")
            }
          />
        </div>
      ),
    },
    {
      title: (
        <div className="settings">
          <i className="fa-solid fa-gear"></i>
        </div>
      ),
      key: "action",
      render: (_, record) => (
        <div className="settings_icons">
          <div className="edit" onClick={() => handleEditClick(record)}>
            <i className="fa-solid fa-pen-to-square"></i>
          </div>
          <Popconfirm
            title="Bu servisi siləcəyinizə əminsiniz?"
            onConfirm={() => handleDeleteClick(record)}
            onCancel={() => message.info("Silmə əməliyyatı ləğv edildi.")}
            okText="Bəli"
            cancelText="Xeyr"
            placement="topRight"
          >
            <div className="delete">
              <i className="fa-solid fa-trash"></i>
            </div>
          </Popconfirm>
        </div>
      ),
    },
  ];

  const handleAddClick = () => {
    setIsEditing(false);
    setEditingRecord(null);
    setIsModalVisible(true);
    resetForm();
    setLangCode(primaryLangCode);
    setFieldValue("cover_image", "");
    setLogoFileList([]);
    setSelectedColorName(undefined);
    setSelectedName(undefined);
  };

  const handleEditClick = (record: ServicesDataType) => {
    setIsEditing(true);
    setEditingRecord(record);
    setIsModalVisible(true);

    setFieldValue("name", record.name || "");
    setFieldValue("description", record.description || "");
    setFieldValue("text", record.text || "");
    setFieldValue("price", record.price != null ? Number(record.price) : null);
    setFieldValue(
      "discounted_price",
      record.discounted_price != null ? Number(record.discounted_price) : null
    );
    setFieldValue("cover_image", record.cover_image || "");
    setFieldValue("service_group_id", Number(record.service_group_id || 0));
    setFieldValue("is_visible", Boolean(record.is_visible));
    setFieldValue("selected_service", Boolean(record.selected_service));
    setFieldValue("bg_color", Number(record.bg_color || 0));
    setFieldValue("translations", record.translations || []);
    setLangCode(primaryLangCode);

    setLogoFileList(
      record.cover_image
        ? [
            {
              uid: "-1",
              name: `${record.name}`,
              status: "done",
              url: record.cover_image,
            },
          ]
        : []
    );

    initialSpecsValues.current = {
      name: record.name || "",
      description: record.description || "",
      text: record.text || "",
      price: record.price != null ? Number(record.price) : 0,
      bg_color: Number(record.bg_color || 0),
      service_group_id: Number(record.service_group_id || 0),
      discounted_price:
        record.discounted_price != null ? Number(record.discounted_price) : 0,
      cover_image: record.cover_image || "",
      is_visible: Boolean(record.is_visible),
      translations:
        record.translations?.map((l) => ({
          lang_code: l.lang_code,
          name: l.name,
          text: l.text,
          description: l.description,
        })) || [],
    };
  };

  return (
    <div id="pages">
      <Helmet>
        <title>Orhan Door | Servislər</title>
      </Helmet>
      {loading ? (
        <Spinner />
      ) : (
        <div>
          <div className="page_head">
            <h3 className="page_title">Servislər</h3>
          </div>
          <div className="pages_components">
            <MainButton onClick={handleAddClick}>
              <FaPlus size={20} />
            </MainButton>
            <div className="pages_table">
              <Table<ServicesDataType>
                scroll={{ x: "100%" }}
                columns={columns}
                dataSource={services}
                rowKey={(record) => record.id.toString()}
                pagination={false}
              />
            </div>
          </div>
        </div>
      )}

      <Modal
        title={isEditing ? "Servisni redaktə edin" : "Yeni Servis əlavə edin"}
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setSelectedName(undefined);
          setSelectedColorName(undefined);
        }}
        footer={null}
        style={{ minWidth: "340px" }}
        width={"80%"}
        centered
      >
        <form className="modal_form" onSubmit={handleSubmit}>
          <ChangeLanguage
            langCode={langCode}
            setLangCode={setLangCode}
            values={values}
            setFieldValue={setFieldValue}
            langs={langs}
          />
          <div className="inputs">
            <div className="form_input services_form_input">
              <label className="label" htmlFor="name">
                Servis adı {langCode}
              </label>
              <div className="input_wrapper">
                <input
                  name="name"
                  id="name"
                  placeholder="Servis adı"
                  value={
                    langCode === primaryLangCode
                      ? values.name
                      : values.translations.find(
                          (t) => t.lang_code === langCode
                        )?.name || ""
                  }
                  onChange={(e) =>
                    handleTranslationChange(e.target.value, "name")
                  }
                />
                <span className="focus_border"></span>
              </div>
              <ErrorMessage error={errors.name} touched={touched.name} />
            </div>

            <div className="form_input services_form_input">
              <label className="label" htmlFor="description">
                Servis təsviri {langCode}
              </label>
              <div className="input_wrapper">
                <input
                  name="description"
                  id="description"
                  placeholder="Servis təsviri"
                  value={
                    langCode === primaryLangCode
                      ? values.description
                      : values.translations.find(
                          (t) => t.lang_code === langCode
                        )?.description || ""
                  }
                  onChange={(e) =>
                    handleTranslationChange(e.target.value, "description")
                  }
                />
                <span className="focus_border"></span>
              </div>
              <ErrorMessage
                error={errors.description}
                touched={touched.description}
              />
            </div>

            <div className="form_input services_form_input">
              <label className="label" htmlFor="text">
                Servis mətni {langCode}
              </label>
              <div className="input_wrapper">
                <input
                  type="text"
                  name="text"
                  id="text"
                  placeholder="Servis mətni"
                  value={
                    langCode === primaryLangCode
                      ? values.text
                      : values.translations.find(
                          (t) => t.lang_code === langCode
                        )?.text || ""
                  }
                  onChange={(e) =>
                    handleTranslationChange(e.target.value, "text")
                  }
                />
                <span className="focus_border"></span>
              </div>
              <ErrorMessage error={errors.text} touched={touched.text} />
            </div>

            <div className="form_input services_form_input">
              <label className="label" htmlFor="price">
                Qiymət
              </label>
              <div className="input_wrapper">
                <input
                  type="number"
                  name="price"
                  id="price"
                  value={values.price ?? ""}
                  onChange={handleChange}
                  placeholder="Qiymət"
                />
                <span className="focus_border"></span>
              </div>
              <ErrorMessage error={errors.price} touched={touched.price} />
            </div>

            <div className="form_input services_form_input">
              <label className="label" htmlFor="discounted_price">
                Endirim
              </label>
              <div className="input_wrapper">
                <input
                  type="number"
                  name="discounted_price"
                  id="discounted_price"
                  value={values.discounted_price ?? ""}
                  onChange={handleChange}
                  placeholder="Endirim"
                />
                <span className="focus_border"></span>
              </div>
              <ErrorMessage
                error={errors.discounted_price}
                touched={touched.discounted_price}
              />
            </div>

            {/* Qrup adı və rəng seçimi YALNIZ REDAKTƏ MODUNDA GÖRÜNSÜN */}
            {isEditing && (
              <>
                <div className="form_input services_form_input">
                  <SelectColor
                    setFieldValue={setFieldValue}
                    color_id={editingRecord?.bg_color ?? null}
                    selectedName={selectedColorName}
                    setSelectedName={setSelectedColorName}
                    field={"bg_color"}
                  />
                </div>

                <div className="form_input services_form_input">
                  <SelectServicesGroupName
                    setFieldValue={setFieldValue}
                    group_name_id={editingRecord?.service_group_id ?? null}
                    selectedName={selectedName}
                    setSelectedName={setSelectedName}
                  />
                </div>
              </>
            )}
          </div>

          <div className="form_input">
            <SelectImage
              image_name="Şəkil seçin"
              logoFileList={logoFileList}
              setLogoFileList={setLogoFileList}
              field="cover_image"
              setFieldValue={setFieldValue}
            />
            <ErrorMessage
              error={errors.cover_image}
              touched={touched.cover_image}
            />
          </div>

          <div className="form_input">
            <label className="active_label" htmlFor="">
              Xidmətin saytda görünməsi
            </label>
            <Switch
              checked={values.is_visible}
              onChange={(checked) => setFieldValue("is_visible", checked)}
            />
          </div>

          <div className="form_input">
            <label className="active_label" htmlFor="">
              Seçilmiş xidmət olaraq göstər
            </label>
            <Switch
              checked={values.selected_service}
              onChange={(checked) => setFieldValue("selected_service", checked)}
            />
          </div>

          <MainButton full type="submit" className="form_add_btn">
            {isEditing ? " Redaktə edin" : "Əlavə edin"}
          </MainButton>
        </form>
      </Modal>

      <ShowImage
        showImageModal={showImageModal}
        setShowImagesModal={setShowImageModal}
        image={selectedImage}
      />
    </div>
  );
};

export default Services;
